// src/stores/useWellFinderStore.js
import { create } from "zustand";
 
/*
  AIPrime WellFinder product state.
 
  Responsibilities:
  - Track which well is selected (detected or RRC)
  - Track filter state (match status filter)
  - Hold offset lines GeoJSON (built client-side from vtile features)
  - Track whether AIPrime WellFinder owns the RRC layer visibility
    (so it can restore prior state on unmount)
*/
 
import { useWellSelectionStore } from "./useWellSelectionStore";
 
export const useWellFinderStore = create((set, get) => ({
 
  // ── Selected well ──────────────────────────────────────────────────
  selectedWell:     null,
  selectedWellId:   null,
  selectedWellType: null,
  selectedRrcOnly:  false,   // true when an unmatched RRC well is clicked
 
  selectWell: (id, type, detail) => {
    set({
      selectedWellId:   id,
      selectedWellType: type,
      selectedWell:     detail,
      selectedRrcOnly:  false,
    });
 
    // ── Side effect: open linked RRC well in WellInfoPanel ──────────
    // When a detected well has a confirmed RRC link, automatically open
    // the enriched RRC panel on the right so both are visible together.
    const rrcAssetId = detail?.link?.rrc_asset_id;
 
    if (rrcAssetId) {
      // Build a minimal GeoJSON feature so WellInfoPanel / usePanelEnrichment
      // can sniff the asset ID. The hook looks for properties.id first.
      useWellSelectionStore.getState().selectWell({
        id: rrcAssetId,
        properties: {
          id:          rrcAssetId,
          asset_type:  "rrc_well_surface",
          // Carry through any display fields already on the detail so the
          // panel header renders instantly before the enrichment fetch lands.
          name:        detail.rrc?.well_name ?? detail.rrc?.name ?? null,
          api:         detail.rrc?.api ?? detail.link?.rrc_source_id ?? null,
          status_norm: detail.rrc?.status_norm ?? null,
          confidence:  1,
        },
        lngLat: detail.rrc?.lng != null
          ? { lng: detail.rrc.lng, lat: detail.rrc.lat }
          : null,
      });
    } else if (type !== "rrc") {
      // Only clear the right panel for detected wells with no RRC link.
      // When the user clicked an RRC well directly, it was already opened
      // in WellInfoPanel by the map click handler — don't clobber it here.
      useWellSelectionStore.getState().clearWell();
    }
  },
 
  selectRrcOnly: (id) => {
    set({
      selectedWellId:   id,
      selectedWellType: "rrc",
      selectedWell:     null,
      selectedRrcOnly:  true,
    });
    // WellInfoPanel is already populated by useWellSelectionStore — don't clear it
  },
 
  clearSelection: () => {
    set({
      selectedWellId:   null,
      selectedWellType: null,
      selectedWell:     null,
      selectedRrcOnly:  false,
    });
    // Clear the right panel too
    useWellSelectionStore.getState().clearWell();
  },
 
  // ── Filter state ───────────────────────────────────────────────────
  activeFilters: [],
 
  setFilter: (statuses) => set({ activeFilters: statuses }),
 
  toggleFilter: (status) => set((state) => {
    const current = new Set(state.activeFilters);
    if (current.has(status)) {
      current.delete(status);
    } else {
      current.add(status);
    }
    return { activeFilters: Array.from(current) };
  }),
 
  clearFilters: () => set({ activeFilters: [] }),
 
  // ── Offset lines ───────────────────────────────────────────────────
  offsetLines:      null,
  setOffsetLines:   (fc) => set({ offsetLines: fc }),
  clearOffsetLines: ()   => set({ offsetLines: null }),
 
  // ── RRC layer ownership ────────────────────────────────────────────
  // The RRC SURFACE-POINT geometry as WellFinder found it, so leaving restores
  // it. Was `priorRrcEnabled`, which stored the wrong thing: `enabled` is a
  // DERIVED summary of the geometry sub-toggles, and MapView's visibility
  // effect computes `enabled && geometry.point`. Remembering the summary and
  // restoring that could not put the layer back.
  priorRrcPointOn:    null,
  savePriorRrcState:  (pointOn) => set({ priorRrcPointOn: pointOn }),
  clearPriorRrcState: ()        => set({ priorRrcPointOn: null }),
 
  // ── Loading state ──────────────────────────────────────────────────
  loadingDetail:    false,
  detailError:      null,
  setLoadingDetail: (v) => set({ loadingDetail: v }),
  setDetailError:   (e) => set({ detailError: e }),
 
  // ── Stats ──────────────────────────────────────────────────────────
  stats: { strong: 0, candidate: 0, unverified: 0, total: 0 },
  setStats: (stats) => set({ stats }),

  // ── Active panel tab ───────────────────────────────────────────────
  // 'core' | 'regulatory' | 'pro'. The PRO tab hosts Well3D (formerly its
  // own map mode).
  activeTab: 'core',
  setActiveTab: (tab) => set({ activeTab: tab }),
 
}));